import { useCallback, useEffect, useRef } from 'react'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { adminTourSteps, customerTourSteps } from './tourSteps'
import { DEMO_TOUR_ADMIN_KEY, DEMO_TOUR_CLIENT_KEY } from '../../lib/demo'

type TourVariant = 'admin' | 'customer'

export function useDemoTour(variant: TourVariant, enabled: boolean) {
  const driverRef = useRef<ReturnType<typeof driver> | null>(null)
  const hasStartedRef = useRef(false)

  const storageKey = variant === 'admin' ? DEMO_TOUR_ADMIN_KEY : DEMO_TOUR_CLIENT_KEY
  const steps = variant === 'admin' ? adminTourSteps : customerTourSteps

  const startTour = useCallback(() => {
    // Skip on mobile — sidebar isn't in the DOM
    if (window.innerWidth < 768) return

    // Destroy previous instance if any
    if (driverRef.current) {
      driverRef.current.destroy()
    }

    const d = driver({
      showProgress: true,
      steps,
      animate: true,
      smoothScroll: true,
      allowClose: true,
      stagePadding: 6,
      stageRadius: 8,
      popoverOffset: 12,
      onDestroyed: () => {
        localStorage.setItem(storageKey, 'true')
      },
    })

    driverRef.current = d
    d.drive()
  }, [steps, storageKey])

  // Auto-start on first visit with retry logic
  useEffect(() => {
    if (!enabled || hasStartedRef.current) return

    const alreadySeen = localStorage.getItem(storageKey) === 'true'
    if (alreadySeen) return

    // Skip on mobile
    if (window.innerWidth < 768) return

    hasStartedRef.current = true

    let attempt = 0
    const maxAttempts = 5

    function tryStart() {
      attempt++
      const firstEl = steps[0]?.element
      const found = typeof firstEl === 'string' ? document.querySelector(firstEl) : null

      if (found) {
        startTour()
      } else if (attempt < maxAttempts) {
        setTimeout(tryStart, 1000)
      }
    }

    const timer = setTimeout(tryStart, 1500)

    return () => {
      clearTimeout(timer)
      // Reset so React StrictMode re-mount can retry
      hasStartedRef.current = false
    }
  }, [enabled, storageKey, steps, startTour])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return { startTour }
}
